import { useEffect } from "react";
import { useNavigate } from "react-router";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { clearDistrict, setDistrict } from "../../app/userSlice";
import BroadcastAlerts from "../../components/BroadcastAlerts";
import { ClassLinkOrCleverIcon } from "../../components/ClassLinkOrCleverIcon";
import { Loading } from "../../components/Loading";
import SwitchToLegacy from "../../components/SwitchToLegacy";
import { IMutationError } from "../courses/Courses";
import {
	SelectDistrictRowSpaViewModel,
	useMakeDistrictSelectionMutation,
	useGetSelectDistrictOptionsQuery,
} from "./districtsApiSlice";

export default function SelectDistrict() {
	const { firstName, lastName, isDistrictInTransition } = useAppSelector((state) => state.user);
	const { aspUrl, config } = useAppSelector((state) => state.site);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [selectDistrict] = useMakeDistrictSelectionMutation();
	const districtsResult = useGetSelectDistrictOptionsQuery();
	const data = districtsResult.data;

	if (sessionStorage.getItem("prefersSpa") === "false") {
		window.location.href = `${aspUrl}/SignIn/SelectDistrict`;
	}

	useEffect(() => {
		if (data?.districts.length === 1) {
			selectAndNavigateToDistrict(data.districts[0]);
		}
	}, [data]);

	const selectAndNavigateToDistrict = (district: SelectDistrictRowSpaViewModel) => {
		handleSelectDistrict(district);
	};

	const cleverOrClassLinkBodyTemplate = (data: SelectDistrictRowSpaViewModel) => {
		return (
			<ClassLinkOrCleverIcon
				isClassLinkEntity={!!data.classLinkId}
				isCleverEntity={!!data.cleverId}
			/>
		);
	};

	const districtNameBodyTemplate = (data: SelectDistrictRowSpaViewModel) => {
		return (
			<a
				id={`choose-district-${data.id}`}
				className="link"
				onClick={() => selectAndNavigateToDistrict(data)}>
				{data.name}
			</a>
		);
	};

	const handleSelectDistrict = (data: SelectDistrictRowSpaViewModel) => {
		selectDistrict(data.id)
			.unwrap()
			.then(() => {
				dispatch(
					setDistrict({
						id: data.id,
						name: data.name,
					})
				);
				navigate("/adminHome");
			})
			.catch((error) => handleSelectDistrictError(error));
	};

	const handleSelectDistrictError = (error: IMutationError) => {
		dispatch(clearDistrict());
		if (error.data) {
			console.log(`Error: ${error.data}`);
		} else {
			console.log(`Error: an unexpected error occurred.`);
		}
	};

	if (isDistrictInTransition) return null;

	return (
		<>
			<BroadcastAlerts className="w-full" alerts={data?.alerts} />
			<section className="text-s sm:text-left w-full">
				<div className="flex justify-between">
					<div className="w-3/4">
						<h1 className="font-bold">
							Welcome, {firstName} {lastName}
						</h1>
					</div>
				</div>
				<h2 className="text-s sm:text-left">Select a District</h2>
			</section>
			<section>
				<Loading loading={districtsResult.isLoading} />
				{data && (
					<DataTable
						value={data.districts}
						dataKey="id"
						emptyMessage="No districts found"
						stripedRows
						className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
						style={{ paddingTop: "16px" }}>
						{!config?.isUtahComposeSite &&
							data.districts.some((d) => d.classLinkId || d.cleverId) && (
								<Column
									body={cleverOrClassLinkBodyTemplate}
									headerClassName="!pr-0 w-4"
									className="!pr-0"></Column>
							)}
						<Column header="Name" field="name" body={districtNameBodyTemplate} />
						<Column header="School Count" field="schoolCount" />
						<Column header="District Code" field="code" />
					</DataTable>
				)}
			</section>
			<SwitchToLegacy link="/SignIn/SelectDistrict" />
		</>
	);
}
